import { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { NAVIGATION } from '../lib/nav';

/** The home screen owns three paths: the resolved one, the forced overview and a saved view. */
const isHomePath = (p: string) => p === '/app' || p === '/app/overview' || p.startsWith('/app/v/');
import { useAuth } from '../stores/auth';
import { AssistantDrawer } from './AssistantDrawer';
import { Logo } from './site/Logo';

function Icon({ name, className }: { name: string; className?: string }) {
  const C = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[name];
  return C ? <C className={className} /> : <Icons.Circle className={className} />;
}

/**
 * The single application shell. There is no per-role layout: the sidebar is derived
 * from the user's permissions, so a technician, an accountant and the CEO all run
 * exactly this component and simply see different things in it.
 */
export function AppLayout() {
  const { user, logout, canAccessPage, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const scope = user?.scope.type;
  const groups = NAVIGATION
    .map((g) => ({ ...g, items: g.items.filter((i) => canAccessPage(i.page) && (!i.onlyScope || scope === i.onlyScope) && (!i.hideForScope || scope !== i.hideForScope)) }))
    .filter((g) => g.items.length > 0);

  const current = useMemo(() => {
    const all = NAVIGATION.flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })));
    const match = all.filter((i) => i.path === '/app' ? isHomePath(location.pathname) : location.pathname.startsWith(i.path)).sort((a, b) => b.path.length - a.path.length)[0];
    return match ?? { group: 'Overview', label: 'Dashboard' };
  }, [location.pathname]);

  // ⌘K / Ctrl+K opens the assistant from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); if (can('assistant.use')) setAssistantOpen(true); }
      if (e.key === 'Escape') setAssistantOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [can]);

  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('') || user?.email?.[0]?.toUpperCase() || '?';
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;
  const region = user?.region === 'AE' ? 'United Arab Emirates' : 'United Kingdom';

  return (
    <div className="flex h-screen overflow-hidden bg-ink-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-[264px] transform flex-col bg-ink-900 text-white transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="safe-top flex h-16 shrink-0 items-center justify-between border-b border-white/[0.08] px-5">
          <Logo tone="white" className="h-7" to="/app" />
          <button className="rounded-full p-1.5 text-white/50 hover:bg-white/10 hover:text-white lg:hidden" onClick={() => setSidebarOpen(false)}><Icons.X className="h-4 w-4" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5">
          {groups.map((group) => (
            <div key={group.group} className="mb-5">
              <div className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/30">{group.group}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/app'}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `relative mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] transition ${
                      isActive || (item.path === '/app' && isHomePath(location.pathname))
                        ? 'bg-white/[0.09] font-medium text-white before:absolute before:bottom-2 before:left-0 before:top-2 before:w-[3px] before:rounded-full before:bg-bronze-400'
                        : 'text-white/55 hover:bg-white/[0.05] hover:text-white'
                    }`
                  }
                >
                  <Icon name={item.icon} className="h-4 w-4 shrink-0" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="safe-bottom shrink-0 border-t border-white/[0.08] p-3">
          <div className="flex items-center gap-3 rounded-xl px-2 py-1.5">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-bronze-400 font-display text-[15px] text-ink-900">{initials}</div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[13px] font-medium text-white">{fullName}</div>
              <div className="truncate text-[11px] text-white/45">{user?.roleName}</div>
            </div>
            <button onClick={async () => { await logout(); navigate('/login'); }} className="rounded-full p-2 text-white/40 transition hover:bg-white/10 hover:text-white" title="Sign out">
              <Icons.LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-30 bg-ink-900/40 backdrop-blur-[2px] lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="safe-top flex h-16 shrink-0 items-center gap-3 border-b border-ink-200/80 bg-white/85 px-4 backdrop-blur-md lg:px-8">
          <button className="rounded-full p-2 text-ink-500 hover:bg-ink-100 lg:hidden" onClick={() => setSidebarOpen((v) => !v)}>
            <Icons.Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 leading-tight">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ink-400">{current.group}</div>
            <div className="display truncate text-[20px] text-ink-900">{current.label}</div>
          </div>
          <div className="flex-1" />

          <span className="hidden items-center gap-2 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-[11.5px] text-ink-500 md:inline-flex">
            <span className="h-1.5 w-1.5 rounded-full bg-bronze-500" />{region}
          </span>

          {can('assistant.use') && (
            <button
              onClick={() => setAssistantOpen(true)}
              className="group inline-flex items-center gap-2 rounded-full border border-ink-900 bg-ink-900 py-1.5 pl-3 pr-1.5 text-[12.5px] font-medium text-white transition hover:bg-white hover:text-ink-900"
            >
              <Icons.Sparkles className="h-3.5 w-3.5 text-bronze-300 group-hover:text-bronze-500" />
              Ask Lotus
              <kbd className="ml-1 hidden rounded-full bg-white/15 px-2 py-0.5 font-sans text-[10px] text-white/80 group-hover:bg-ink-100 group-hover:text-ink-600 sm:inline">⌘K</kbd>
            </button>
          )}

          <Link to="/" className="hidden rounded-full p-2 text-ink-400 transition hover:bg-ink-100 hover:text-ink-900 md:block" title="View the website">
            <Icons.Globe className="h-4 w-4" />
          </Link>
        </header>

        <main className="flex-1 overflow-y-auto px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>

      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  );
}
